if (!customElements.get('noa-subscription-plans')) {
  class NoaSubscriptionPlans extends HTMLElement {
    connectedCallback() {
      this.interval = this.dataset.defaultInterval === 'yearly' ? 'yearly' : 'monthly';
      this.buttons = [...this.querySelectorAll('[data-billing-interval]')];
      this.billingSwitch = this.querySelector('[data-billing-switch]');
      this.cards = [...this.querySelectorAll('[data-plan-code]')];
      this.dialog = this.querySelector('[data-email-dialog]');
      this.emailForm = this.querySelector('[data-email-form]');
      this.emailStatus = this.querySelector('[data-email-status]');
      this.emailDialogCopy = this.querySelector('[data-email-dialog-copy]');
      this.managementPanel = this.querySelector('[data-management-panel]');
      this.managementStatus = this.querySelector('[data-management-status]');
      this.managementBadge = this.querySelector('[data-management-badge]');
      this.manageEntry = this.querySelector('[data-manage-entry]');
      this.manageLink = this.querySelector('[data-manage-link]');
      // NoaAPI hands over a short-lived session in the URL fragment, which never
      // reaches Shopify's servers or a Referer (Oneleet BL-018). It is sent back
      // only as an Authorization header, never in a URL.
      this.session = new URLSearchParams(window.location.hash.slice(1)).get('session');
      this.managementRequested = new URL(window.location.href).searchParams.get('subscription_view') === 'manage';
      this.sessionKey = 'noa-billing-session:' + window.location.pathname;
      try {
        if (this.session) sessionStorage.setItem(this.sessionKey, this.session);
        else this.session = sessionStorage.getItem(this.sessionKey);
      } catch (_) { /* Private browsing can disable session storage. */ }
      this.pendingSelection = null;
      this.currentSubscription = null;
      this.updatePending = false;

      const cleanUrl = new URL(window.location.href);
      if (cleanUrl.hash || cleanUrl.searchParams.has('session')) {
        cleanUrl.hash = '';
        cleanUrl.searchParams.delete('session');
        window.history.replaceState({}, document.title, cleanUrl.toString());
      }

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.setInterval(button.dataset.billingInterval));
      });
      this.billingSwitch?.addEventListener('change', () => {
        this.setInterval(this.billingSwitch.checked ? 'yearly' : 'monthly');
      });
      this.querySelector('[data-dialog-close]')?.addEventListener('click', () => this.dialog?.close());
      this.manageLink?.addEventListener('click', () => {
        this.pendingSelection = null;
        this.openEmailDialog(true);
      });
      this.querySelectorAll('[data-management-action]').forEach((button) => {
        button.addEventListener('click', () => this.updateSubscription(button.dataset.managementAction));
      });
      this.emailForm?.addEventListener('submit', (event) => this.requestSecureLink(event));

      this.setInterval(this.interval);
      if (this.session && this.dataset.managementUrl) this.loadSubscription();
    }

    setInterval(interval) {
      if (!['monthly', 'yearly'].includes(interval)) return;

      this.interval = interval;
      if (this.billingSwitch) this.billingSwitch.checked = interval === 'yearly';
      this.buttons.forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.billingInterval === interval));
      });

      this.cards.forEach((card) => {
        const enabled = card.dataset[interval + 'Enabled'] === 'true';
        const amount = card.dataset[interval + 'Price'];
        const period = card.dataset[interval + 'Period'];
        const amountElement = card.querySelector('[data-plan-amount]');
        const periodElement = card.querySelector('[data-plan-period]');
        const cta = card.querySelector('[data-plan-cta]');
        const stateLabel = card.querySelector('[data-plan-state-label]');
        const planCode = card.dataset.planCode;
        const planName = card.querySelector('.noa-plan-card__name')?.textContent?.trim() || 'this plan';

        if (amountElement) amountElement.textContent = amount;
        if (periodElement) periodElement.textContent = period;
        delete card.dataset.planState;
        if (stateLabel) stateLabel.hidden = true;

        if (!cta) return;
        cta.onclick = null;
        if (this.currentSubscription?.hasSubscription) {
          const ranks = { free: 0, plus: 1, pro: 2 };
          const currentRank = ranks[this.currentSubscription.plan] || 0;
          const cardRank = ranks[planCode] || 0;
          if (planCode === this.currentSubscription.plan) {
            card.dataset.planState = 'current';
            if (stateLabel) {
              stateLabel.textContent = 'Your current plan';
              stateLabel.hidden = false;
            }
            cta.textContent = 'Current plan';
            cta.setAttribute('aria-disabled', 'true');
            cta.removeAttribute('href');
            return;
          }

          const upgrade = (this.currentSubscription.availableUpgrades || []).find(
            (item) => item.plan === planCode,
          );
          if (cardRank < currentRank && this.currentSubscription.canDowngrade && enabled && !this.updatePending) {
            cta.textContent = 'Switch to ' + planName + ' at renewal';
            cta.href = '#';
            cta.removeAttribute('aria-disabled');
            cta.onclick = (event) => {
              event.preventDefault();
              this.updateSubscription('downgrade', { plan: planCode, interval: this.currentSubscription.interval || interval });
            };
            return;
          }
          const intervalEnabled =
            interval === 'monthly' ? upgrade?.monthlyEnabled : upgrade?.yearlyEnabled;
          if (cardRank > currentRank && upgrade && intervalEnabled && !this.updatePending) {
            card.dataset.planState = 'upgrade';
            if (stateLabel) {
              stateLabel.textContent = 'Upgrade available';
              stateLabel.hidden = false;
            }
            cta.textContent = 'Upgrade to ' + planName;
            cta.href = '#';
            cta.onclick = (event) => {
              event.preventDefault();
              this.updateSubscription('upgrade', { plan: planCode, interval });
            };
            cta.removeAttribute('aria-disabled');
            return;
          }

          cta.textContent = cardRank < currentRank ? 'Included in your plan' : 'Upgrade unavailable';
          cta.setAttribute('aria-disabled', 'true');
          cta.removeAttribute('href');
          return;
        }
        if (
          !enabled ||
          !this.dataset.checkoutBaseUrl ||
          (!this.session && !this.dataset.linkRequestUrl)
        ) {
          cta.setAttribute('aria-disabled', 'true');
          cta.removeAttribute('href');
          return;
        }

        if (!this.session) {
          cta.href = '#';
          cta.onclick = (event) => {
            event.preventDefault();
            this.pendingSelection = { plan: card.dataset.planCode, interval };
            this.openEmailDialog(false);
          };
          cta.removeAttribute('aria-disabled');
          return;
        }

        cta.href = '#';
        cta.onclick = (event) => {
          event.preventDefault();
          this.startCheckout(cta, card.dataset.planCode, interval);
        };
        cta.removeAttribute('aria-disabled');
      });
    }

    authHeaders(extra = {}) {
      return { ...extra, Authorization: 'Bearer ' + this.session };
    }

    clearSession() {
      this.session = null;
      this.currentSubscription = null;
      this.pendingSelection = null;
      try { sessionStorage.removeItem(this.sessionKey); } catch (_) { /* Storage unavailable. */ }
      if (this.manageEntry) this.manageEntry.hidden = false;
      if (this.managementBadge) this.managementBadge.hidden = true;
      this.querySelectorAll('[data-management-action]').forEach((button) => {
        button.hidden = true;
        button.disabled = true;
      });
      const planElement = this.querySelector('[data-management-plan]');
      const detailElement = this.querySelector('[data-management-detail]');
      if (planElement) planElement.textContent = 'Secure link unavailable';
      if (detailElement) detailElement.textContent = 'Request a new secure link to continue.';
      this.setInterval(this.interval);
    }

    async startCheckout(cta, plan, interval) {
      if (cta.getAttribute('aria-disabled') === 'true') return;
      cta.setAttribute('aria-disabled', 'true');
      try {
        const response = await fetch(this.dataset.checkoutBaseUrl, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: this.authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ plan, interval }),
        });
        const payload = await response.json();
        if (!response.ok) {
          if (['billing_session_invalid', 'billing_checkout_already_started'].includes(payload.errorCode)) {
            this.clearSession();
            this.pendingSelection = { plan, interval };
            this.openEmailDialog(false);
            return;
          }
          if (payload.errorCode === 'billing_already_subscribed') {
            this.loadSubscription();
            return;
          }
          throw new Error(payload.message || 'Unable to start checkout');
        }
        const target = new URL(payload.checkoutUrl);
        if (target.protocol !== 'https:') throw new Error('Invalid checkout link.');
        window.location.assign(target.toString());
      } catch (_error) {
        const label = cta.textContent;
        cta.textContent = 'Checkout unavailable. Try again';
        setTimeout(() => { cta.textContent = label; }, 4000);
      } finally {
        cta.removeAttribute('aria-disabled');
      }
    }

    openEmailDialog(managementOnly) {
      if (!this.dataset.linkRequestUrl || !this.dialog) return;
      if (this.emailStatus) this.emailStatus.textContent = '';
      if (this.emailDialogCopy) {
        this.emailDialogCopy.textContent = managementOnly
          ? 'We will email you a secure, expiring link to your subscription.'
          : 'We will email you a secure link for the selected plan. No existing Noa account is required. Use the same email when you sign in to Noa.';
      }
      this.dialog.showModal();
      this.dialog.querySelector('input[type="email"]')?.focus();
    }

    async requestSecureLink(event) {
      event.preventDefault();
      const email = new FormData(this.emailForm).get('email');
      const submit = this.emailForm.querySelector('[type="submit"]');
      const body = {
        email: String(email || ''),
        purpose: this.pendingSelection ? 'plans' : 'manage',
      };
      if (this.pendingSelection) {
        body.plan = this.pendingSelection.plan;
        body.interval = this.pendingSelection.interval;
      }

      submit.disabled = true;
      if (this.emailStatus) this.emailStatus.textContent = 'Sending secure link...';

      try {
        const response = await fetch(this.dataset.linkRequestUrl, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Request failed');
        if (this.emailStatus) {
          this.emailStatus.textContent =
            'If this email can receive subscription links, the secure link is on its way.';
        }
        this.emailForm.reset();
      } catch (_error) {
        if (this.emailStatus) this.emailStatus.textContent = 'Unable to send the link. Please try again.';
      } finally {
        submit.disabled = false;
      }
    }

    async loadSubscription() {
      this.setManagementStatus('Loading your subscription...', 'pending');
      try {
        const response = await fetch(this.dataset.managementUrl, {
          mode: 'cors',
          credentials: 'omit',
          headers: this.authHeaders(),
        });
        const payload = await response.json();
        if (!response.ok) {
          if (payload.errorCode === 'billing_session_invalid') this.clearSession();
          throw new Error(payload.message || 'Unable to load subscription');
        }
        if (this.manageEntry) this.manageEntry.hidden = true;
        this.renderSubscription(payload);
      } catch (error) {
        if (this.managementPanel) this.managementPanel.hidden = false;
        const planElement = this.querySelector('[data-management-plan]');
        const detailElement = this.querySelector('[data-management-detail]');
        if (planElement) planElement.textContent = 'Secure link unavailable';
        if (detailElement) detailElement.textContent = 'Request a new management link below to continue.';
        this.setManagementStatus(error.message || 'Unable to load your subscription.', 'error');
      }
    }

    renderSubscription(subscription, { clearStatus = true } = {}) {
      if (!this.managementPanel) return;
      this.currentSubscription = subscription;
      this.setInterval(this.interval);
      if (!subscription.hasSubscription) {
        this.managementPanel.hidden = true;
        if (clearStatus) this.setManagementStatus('');
        return;
      }
      this.managementPanel.hidden = false;
      const heading = this.querySelector('.noa-plans__title');
      if (heading) heading.textContent = 'Manage your subscription';
      if (this.managementRequested) {
        this.managementPanel.scrollIntoView({ block: 'start' });
        this.managementRequested = false;
      }
      const planName = { plus: 'Noa Plus', pro: 'Noa Pro' }[subscription.plan] || 'Noa';
      const planElement = this.querySelector('[data-management-plan]');
      const detailElement = this.querySelector('[data-management-detail]');
      const cancelButton = this.querySelector('[data-management-action="cancel"]');
      const resumeButton = this.querySelector('[data-management-action="resume"]');
      const cardButton = this.querySelector('[data-management-action="update_card"]');
      if (cardButton) cardButton.hidden = !subscription.canUpdateCard;
      const date = subscription.currentPeriodEndsAt
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(
            new Date(subscription.currentPeriodEndsAt),
          )
        : null;

      if (planElement) planElement.textContent = planName;
      if (this.managementBadge) {
        this.managementBadge.hidden = false;
        this.managementBadge.textContent = subscription.cancelAtPeriodEnd
          ? 'Ends after paid period'
          : 'Active';
        this.managementBadge.dataset.state = subscription.cancelAtPeriodEnd ? 'ending' : 'active';
      }
      if (detailElement) {
        const interval = subscription.interval ? subscription.interval + ' billing. ' : '';
        detailElement.textContent = subscription.cancelAtPeriodEnd
          ? interval + 'Access continues until ' + (date || 'the end of the paid period') + '.'
          : interval + (date ? 'Next renewal: ' + date + '.' : 'Subscription active.');
      }
      if (cancelButton) cancelButton.hidden = !subscription.canCancel;
      if (subscription.pendingPlan && detailElement) {
        const date = new Date(subscription.pendingPlanStartsAt);
        const label = Number.isNaN(date.getTime()) ? 'your next renewal' : date.toLocaleDateString();
        detailElement.textContent += ' Switching to Noa ' + subscription.pendingPlan + ' on ' + label + '. Your current access continues until then.';
      }
      if (resumeButton) resumeButton.hidden = !subscription.canResume;
      if (clearStatus) this.setManagementStatus('');
    }

    setManagementStatus(message, state = '') {
      if (!this.managementStatus) return;
      this.managementStatus.textContent = message;
      if (state) {
        this.managementStatus.dataset.state = state;
      } else {
        delete this.managementStatus.dataset.state;
      }
    }

    async updateSubscription(action, selection = null) {
      if (!['cancel', 'resume', 'upgrade', 'downgrade', 'update_card'].includes(action) || this.updatePending) return;
      if (action === 'downgrade' && !window.confirm('Switch to Noa Plus at your next renewal? Your current plan stays active until then.')) return;
      if (
        action === 'cancel' &&
        !window.confirm('Cancel renewal? Your paid access will continue through the current billing period.')
      ) {
        return;
      }
      if (
        action === 'upgrade' &&
        !window.confirm(
          'Upgrade to Noa Pro with ' +
            selection.interval +
            ' billing? Airwallex will charge the prorated difference now and keep your renewal date.',
        )
      ) {
        return;
      }

      this.updatePending = true;
      const buttons = [...this.querySelectorAll('[data-management-action]')];
      buttons.forEach((button) => {
        button.disabled = true;
      });
      this.setManagementStatus(
        {
          cancel: 'Scheduling cancellation...',
          resume: 'Keeping your subscription...',
          upgrade: 'Upgrading your subscription...',
          downgrade: 'Scheduling your plan change...',
          update_card: 'Opening secure card setup...',
        }[action],
        'pending',
      );

      try {
        const requestBody = { action };
        if (action === 'upgrade' || action === 'downgrade') {
          requestBody.plan = selection.plan;
          requestBody.interval = selection.interval;
        }
        const response = await fetch(this.dataset.managementUrl, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: this.authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(requestBody),
        });
        const payload = await response.json();
        if (!response.ok) {
          if (payload.errorCode === 'billing_session_invalid') {
            this.clearSession();
            this.openEmailDialog(true);
          }
          throw new Error(payload.message || 'Unable to update subscription');
        }
        if (action === 'update_card') {
          const target = new URL(payload.checkoutUrl);
          if (target.protocol !== 'https:') throw new Error('Invalid card setup link.');
          window.location.assign(target.toString());
          return;
        }
        const expectedCancellation = action === 'cancel';
        const confirmed =
          action === 'downgrade'
            ? payload.pendingPlan === selection.plan
            : action === 'upgrade'
            ? payload.plan === selection.plan && payload.cancelAtPeriodEnd === false
            : payload.cancelAtPeriodEnd === expectedCancellation;
        if (!confirmed) {
          throw new Error('The subscription provider did not confirm this change. Please try again.');
        }
        this.renderSubscription(payload, { clearStatus: false });
        this.setManagementStatus(
          {
            cancel: 'Renewal cancelled. Your paid access remains active until the date shown.',
            resume: 'Subscription kept. Automatic renewal is active again.',
            upgrade: 'Upgrade complete. Your Noa Pro access is active now.',
            downgrade: 'Plan change scheduled. Your current access stays active until renewal.',
          }[action],
          'success',
        );
        this.managementStatus?.focus();
      } catch (error) {
        this.setManagementStatus(
          error.message || 'Unable to update your subscription. No change was confirmed.',
          'error',
        );
        this.managementStatus?.focus();
      } finally {
        this.updatePending = false;
        buttons.forEach((button) => {
          button.disabled = false;
        });
        this.setInterval(this.interval);
      }
    }
  }

  customElements.define('noa-subscription-plans', NoaSubscriptionPlans);
}
